import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import './popupPlantilla.scss';

export default function PopupPlantilla({ open, onClose, plantilla, onSave }) {
  const [multimediaFile, setMultimediaFile] = useState(null);
  const [isLoadingMultimedia, setIsLoadingMultimedia] = useState(false);
  const [existingMultimediaUrl, setExistingMultimediaUrl] = useState(null);
  const [multimediaType, setMultimediaType] = useState(null);
  const fileInputRef = useRef(null);

  // Debug logs
  useEffect(() => {
    console.log('🔍 [PopupPlantilla] Props recibidas:', { 
      open, 
      plantilla: !!plantilla,
      plantillaData: plantilla 
    });
    console.log('🔍 [PopupPlantilla] Estado del componente:', {
      multimediaType,
      existingMultimediaUrl,
      multimediaFile: !!multimediaFile
    });
  }, [open, plantilla, multimediaType, existingMultimediaUrl, multimediaFile]);

  // Cargar datos de la plantilla cuando se abre el popup
  useEffect(() => {
    if (open && plantilla) {
      console.log('🔍 [PopupPlantilla] Cargando datos de plantilla:', plantilla);
      
      // Determinar el tipo de multimedia
      const tipoContenido = plantilla.tipo_contenido_multimedia?.toLowerCase();
      console.log('🔍 [PopupPlantilla] Tipo de contenido:', tipoContenido);
      
      if (tipoContenido && tipoContenido !== 'ninguna') {
        setMultimediaType(tipoContenido);
        
        // Si hay URL de multimedia, cargarla
        if (plantilla.url_media || plantilla.file_path) {
          const mediaUrl = plantilla.url_media || plantilla.file_path;
          console.log('🔍 [PopupPlantilla] URL original:', mediaUrl);
          
          // Construir la URL completa
          let fullUrl = mediaUrl;
          if (!mediaUrl.startsWith('http')) {
            // Si empieza con /public, usar directamente (el proxy de Vite lo maneja)
            if (mediaUrl.startsWith('/public')) {
              fullUrl = mediaUrl;
            } else if (mediaUrl.startsWith('/')) {
              // Si ya tiene / al inicio, usar directamente
              fullUrl = mediaUrl;
            } else {
              // Si no tiene /, agregarlo
              fullUrl = `/${mediaUrl}`;
            }
          }
          
          console.log('✅ [PopupPlantilla] URL final construida:', fullUrl);
          console.log('✅ [PopupPlantilla] URL completa será:', window.location.origin + fullUrl);
          setExistingMultimediaUrl(fullUrl);
        } else {
          console.log('⚠️ [PopupPlantilla] No hay URL de multimedia');
          setExistingMultimediaUrl(null);
        }
      } else {
        console.log('⚠️ [PopupPlantilla] No hay tipo de contenido multimedia o es "ninguna"');
        setMultimediaType(null);
        setExistingMultimediaUrl(null);
      }
      
      // Limpiar archivo nuevo al abrir
      setMultimediaFile(null);
    }
  }, [open, plantilla]);

  // Validar tipo de archivo según el contenido multimedia
  const validateFileType = (file, contentType) => {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();
    
    if (contentType === 'imagen') {
      const validExtensions = ['jpg', 'jpeg', 'png'];
      const validMimeTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      return validExtensions.includes(fileExtension) && validMimeTypes.includes(file.type);
    } else if (contentType === 'video') {
      const validExtensions = ['mp4'];
      const validMimeTypes = ['video/mp4'];
      return validExtensions.includes(fileExtension) && validMimeTypes.includes(file.type);
    } else if (contentType === 'documento') {
      const validExtensions = ['pdf'];
      const validMimeTypes = ['application/pdf'];
      return validExtensions.includes(fileExtension) && validMimeTypes.includes(file.type);
    }
    return false;
  };

  // Manejar cambio de archivo multimedia
  const handleMultimediaFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!validateFileType(selectedFile, multimediaType)) {
        alert(`Tipo de archivo no válido. Por favor selecciona:\n${multimediaType === 'imagen' ? '- JPG, JPEG o PNG' : multimediaType === 'video' ? '- MP4' : '- PDF'}`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      setIsLoadingMultimedia(true);
      setMultimediaFile(selectedFile);
      setTimeout(() => {
        setIsLoadingMultimedia(false);
      }, 500);
    }
  };

  // Manejar drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!validateFileType(droppedFile, multimediaType)) {
        alert(`Tipo de archivo no válido. Por favor selecciona:\n${multimediaType === 'imagen' ? '- JPG, JPEG o PNG' : multimediaType === 'video' ? '- MP4' : '- PDF'}`);
        return;
      }
      
      setIsLoadingMultimedia(true);
      setMultimediaFile(droppedFile);
      setTimeout(() => {
        setIsLoadingMultimedia(false);
      }, 500);
    }
  };

  const handleRemoveMultimediaFile = () => {
    setMultimediaFile(null);
    setIsLoadingMultimedia(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(multimediaFile);
    }
    onClose();
  };

  const getContentTypeName = (tipo) => {
    if (!tipo) return 'N/A';
    const typeMap = {
      'imagen': 'Imagen',
      'video': 'Video',
      'audio': 'Audio',
      'documento': 'Documento'
    };
    return typeMap[tipo.toLowerCase()] || tipo;
  };

  // Formatear el cuerpo del mensaje con negritas y cursivas
  const formatBody = (body) => {
    if (!body) return '';
    return body
      .replace(/(^|\s)\*([^\s*][^*]*[^\s*])\*([\s.,;:!?¡¿]|$)/g, "$1<strong>$2</strong>$3")
      .replace(/(^|\s)\*([^\s*]+)\*([\s.,;:!?¡¿]|$)/g, "$1<strong>$2</strong>$3")
      .replace(/(^|\s)_([^\s_][^_]*[^\s_])_([\s.,;:!?¡¿]|$)/g, "$1<em>$2</em>$3")
      .replace(/(^|\s)_([^\s_]+)_([\s.,;:!?¡¿]|$)/g, "$1<em>$2</em>$3")
      .replace(/\{\{([^}]+)\}\}/g, '<span style="background: #fff3cd; padding: 2px 4px; border-radius: 3px;">{{$1}}</span>');
  };

  console.log('🔍 [PopupPlantilla] Renderizando componente:', { open, hasPlantilla: !!plantilla });

  // Si no está abierto, no renderizar nada
  if (!open) {
    console.log('❌ [PopupPlantilla] No está abierto, retornando null');
    return null;
  }

  // Si está abierto pero no hay plantilla, mostrar mensaje de error
  if (!plantilla) {
    console.log('⚠️ [PopupPlantilla] Abierto pero sin plantilla, mostrando error');
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography>No se pudo cargar la información de la plantilla.</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  console.log('✅ [PopupPlantilla] Renderizando Dialog con plantilla:', plantilla);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          m: 2,
        },
      }}
      sx={{
        zIndex: 1300, // Asegurar que esté por encima de otros elementos
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: 20, color: '#031b4e' }}>
          Editar Plantilla
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Celular con la vista previa */}
          <Box sx={{ flex: multimediaType ? 1 : 'none', display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                width: 290,
                height: 600,
                background: '#000',
                borderRadius: '40px',
                padding: '8px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                position: 'relative',
              }}
            >
              {/* Notch superior */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 120,
                  height: 25,
                  background: '#000',
                  borderRadius: '0 0 20px 20px',
                  zIndex: 10,
                }}
              />
              {/* Pantalla del celular */}
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  background: '#fff',
                  borderRadius: '32px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Espacio para el notch */}
                <Box sx={{ height: '25px', background: '#fff' }} />
                {/* Header de WhatsApp */}
                <Box
                  sx={{
                    background: '#fff',
                    padding: '12px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <IconButton
                    sx={{
                      color: '#54656f',
                      padding: '4px',
                      minWidth: '32px',
                      '&:hover': { background: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                    </svg>
                  </IconButton>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: '#6a8bff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                  >
                    N
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        sx={{
                          color: '#111b21',
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: 1.2,
                        }}
                      >
                        NOVALY
                      </Typography>
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          background: '#25d366',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          ml: 0.5,
                        }}
                      >
                        <svg width="8" height="8" fill="#fff" viewBox="0 0 24 24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        color: '#667781',
                        fontSize: 12,
                        lineHeight: 1.2,
                      }}
                    >
                      Cuenta de empresa
                    </Typography>
                  </Box>
                  <IconButton
                    sx={{
                      color: '#54656f',
                      padding: '6px',
                      '&:hover': { background: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                    </svg>
                  </IconButton>
                </Box>
                {/* Área de chat */}
                <Box
                  sx={{
                    flex: 1,
                    background: '#efeae2',
                    backgroundImage: `
                      radial-gradient(circle at 20% 30%, rgba(0,0,0,0.02) 1px, transparent 1px),
                      radial-gradient(circle at 80% 70%, rgba(0,0,0,0.02) 1px, transparent 1px),
                      repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,0.015) 35px, rgba(0,0,0,0.015) 36px),
                      repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(0,0,0,0.015) 35px, rgba(0,0,0,0.015) 36px)
                    `,
                    backgroundSize: '100% 100%, 100% 100%, 50px 50px, 50px 50px',
                    padding: '16px 12px',
                    paddingBottom: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {/* Mensaje del template */}
                  <Box
                    sx={{
                      alignSelf: 'flex-start',
                      maxWidth: '87%',
                      width: (multimediaFile || existingMultimediaUrl) ? '100%' : 'auto',
                      background: '#fff',
                      borderRadius: '8px 8px 8px 2px',
                      padding: '8px 12px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    {/* Multimedia existente o nuevo */}
                    {isLoadingMultimedia ? (
                      <Box
                        sx={{
                          width: '100%',
                          aspectRatio: '16/9',
                          minHeight: 150,
                          maxHeight: 200,
                          borderRadius: '4px',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f5f5f5',
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress size={40} sx={{ color: '#3451a1', mb: 1 }} />
                          <Typography sx={{ fontSize: 12, color: '#667781' }}>
                            Cargando...
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        {/* Mostrar nuevo archivo si existe, sino mostrar el existente */}
                        {multimediaType === 'imagen' && (
                          <>
                            {multimediaFile ? (
                              <Box
                                component="img"
                                src={URL.createObjectURL(multimediaFile)}
                                alt="Preview"
                                sx={{
                                  width: '100%',
                                  display: 'block',
                                  borderRadius: '4px',
                                  mb: 1,
                                  maxHeight: 200,
                                  objectFit: 'cover',
                                }}
                              />
                            ) : existingMultimediaUrl ? (
                              <Box
                                component="img"
                                src={existingMultimediaUrl}
                                alt="Plantilla"
                                onError={(e) => {
                                  console.error('❌ [PopupPlantilla] Error al cargar imagen:', existingMultimediaUrl);
                                  console.error('❌ [PopupPlantilla] Error details:', e);
                                  // No ocultar la imagen, solo mostrar un mensaje de error
                                  e.target.style.opacity = '0.5';
                                }}
                                onLoad={() => {
                                  console.log('✅ [PopupPlantilla] Imagen cargada exitosamente:', existingMultimediaUrl);
                                }}
                                sx={{
                                  width: '100%',
                                  display: 'block',
                                  borderRadius: '4px',
                                  mb: 1,
                                  maxHeight: 200,
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              <Box sx={{ 
                                width: '100%', 
                                height: 150, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                background: '#f5f5f5',
                                borderRadius: '4px',
                                mb: 1
                              }}>
                                <Typography sx={{ fontSize: 12, color: '#667781' }}>
                                  No hay imagen disponible
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                        {multimediaType === 'video' && (
                          <>
                            {multimediaFile ? (
                              <Box
                                component="video"
                                src={URL.createObjectURL(multimediaFile)}
                                controls
                                sx={{
                                  width: '100%',
                                  display: 'block',
                                  borderRadius: '4px',
                                  mb: 1,
                                  maxHeight: 200,
                                }}
                              />
                            ) : existingMultimediaUrl ? (
                              <Box
                                component="video"
                                src={existingMultimediaUrl}
                                controls
                                onError={(e) => {
                                  console.error('❌ [PopupPlantilla] Error al cargar video:', existingMultimediaUrl);
                                }}
                                onLoadedData={() => {
                                  console.log('✅ [PopupPlantilla] Video cargado exitosamente:', existingMultimediaUrl);
                                }}
                                sx={{
                                  width: '100%',
                                  display: 'block',
                                  borderRadius: '4px',
                                  mb: 1,
                                  maxHeight: 200,
                                }}
                              />
                            ) : null}
                          </>
                        )}
                        {multimediaType === 'documento' && (
                          <>
                            {multimediaFile ? (
                              <Box
                                sx={{
                                  width: '100%',
                                  borderRadius: '4px',
                                  padding: '16px',
                                  background: '#f5f5f5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  mb: 1,
                                }}
                              >
                                <InsertDriveFileIcon sx={{ fontSize: 40, color: '#3451a1' }} />
                                <Box>
                                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#111b21' }}>
                                    {multimediaFile.name}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, color: '#667781' }}>
                                    {(multimediaFile.size / 1024).toFixed(2)} KB
                                  </Typography>
                                </Box>
                              </Box>
                            ) : existingMultimediaUrl ? (
                              <Box
                                sx={{
                                  width: '100%',
                                  borderRadius: '4px',
                                  padding: '16px',
                                  background: '#f5f5f5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  mb: 1,
                                }}
                              >
                                <InsertDriveFileIcon sx={{ fontSize: 40, color: '#3451a1' }} />
                                <Box>
                                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#111b21' }}>
                                    Documento
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, color: '#667781' }}>
                                    Click para descargar
                                  </Typography>
                                </Box>
                              </Box>
                            ) : null}
                          </>
                        )}
                      </>
                    )}
                    {/* Cuerpo del mensaje */}
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: '#111b21',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: formatBody(plantilla.body || ''),
                      }}
                    />
                  </Box>
                </Box>
                {/* Barra de mensajes */}
                <Box
                  sx={{
                    background: '#efeae2',
                    padding: '8px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <TextField
                    placeholder="Mensaje"
                    size="small"
                    fullWidth
                    disabled
                    sx={{
                      background: '#fff',
                      borderRadius: '24px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '24px',
                        '& fieldset': { border: 'none' },
                      },
                    }}
                  />
                  <IconButton
                    sx={{
                      color: '#fff',
                      background: '#25d366',
                      padding: '10px',
                      minWidth: '40px',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      '&:hover': { background: '#20ba5a' },
                    }}
                  >
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                    </svg>
                  </IconButton>
                </Box>
                {/* Barra de navegación inferior */}
                <Box
                  sx={{
                    width: '100%',
                    height: '4px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: '16px',
                    paddingBottom: '8px',
                  }}
                >
                  <Box
                    sx={{
                      width: '134px',
                      height: '4px',
                      background: '#000',
                      borderRadius: '2px',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Área para subir multimedia (solo si tiene tipo multimedia) */}
          {multimediaType && (
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#031b4e', mb: 2 }}>
                Actualizar {getContentTypeName(multimediaType)}
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 3 }}>
                Puedes subir un nuevo archivo {getContentTypeName(multimediaType).toLowerCase()} para reemplazar el actual.
              </Typography>
              
              <Box
                sx={{
                  border: '1px dashed #bfc8e6',
                  borderRadius: 2,
                  p: 3,
                  background: '#f7f8fc',
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                {!multimediaFile ? (
                  <Box>
                    <CloudUploadIcon
                      sx={{ fontSize: 40, color: '#bfc8e6', mb: 1 }}
                    />
                    <Typography sx={{ color: '#8a8fa7', mb: 1, fontSize: 14 }}>
                      Arrastra y suelta un archivo para subirlo
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        color: '#3451a1',
                        fontSize: 14,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      O elige un archivo de tu dispositivo
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept={
                        multimediaType === 'imagen'
                          ? '.jpg,.jpeg,.png,image/jpeg,image/png'
                          : multimediaType === 'video'
                          ? '.mp4,video/mp4'
                          : '.pdf,application/pdf'
                      }
                      onChange={handleMultimediaFileChange}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: '#fff',
                      borderRadius: 2,
                      p: 2,
                      border: '1px solid #e0e4f7',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {multimediaType === 'imagen' ? (
                        <ImageIcon sx={{ color: '#3451a1' }} />
                      ) : multimediaType === 'video' ? (
                        <VideoLibraryIcon sx={{ color: '#3451a1' }} />
                      ) : (
                        <InsertDriveFileIcon sx={{ color: '#3451a1' }} />
                      )}
                      <Typography>{multimediaFile.name}</Typography>
                    </Box>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveMultimediaFile();
                      }}
                    >
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Botones de acción CORREGIDOS */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
          {/* Botón para descargar audiencia (izquierda) */}
          {plantilla.id_campania && plantilla.upload_audiencia && (
            <Button
              variant="outlined"
              startIcon={<InsertDriveFileIcon />}
              onClick={() => {
                const link = document.createElement('a');
                link.href = `/api/campania/${plantilla.id_campania}/descargar-audiencia`;
                link.download = `audiencia_campania_${plantilla.id_campania}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              sx={{
                borderColor: '#25d366',
                color: '#25d366',
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                padding: '8px 20px',
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#20ba5a',
                  background: '#f0fdf4',
                },
              }}
            >
              Descargar audiencia
            </Button>
          )}
          
          {/* Botones Cancelar y Guardar (derecha) */}
          <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: '#6a8bff',
                color: '#6a8bff',
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                padding: '8px 20px',
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#5a7aef',
                  background: '#f5f8ff',
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                background: '#6a8bff',
                color: '#fff',
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                padding: '8px 20px',
                borderRadius: 2,
                '&:hover': {
                  background: '#5a7aef',
                },
              }}
            >
              Guardar cambios
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}